import { useMemo } from "react";
import { AutoComplete, Select } from "antd";
import axiosInstance from "../../variables/api/axiosInstance";

/**
 * Champs MARQUE et TYPE (modèle) de l'engin — composants CONTRÔLÉS (value / onChange),
 * donc utilisables aussi bien dans un <Form.Item name=…> d'Ant Design (qui injecte
 * value/onChange) qu'avec un state local (modales d'avenant).
 *
 * Ils portent la dépendance MARQUE → TYPE qui n'existait qu'au seul endroit
 * (EnginForm) : les avenants laissaient le Type en SAISIE LIBRE, sans liste de
 * modèles et sans alimenter le référentiel.
 */

// Normalise les 2 formes de marque rencontrées dans le code :
//  - /api/vehicule-marques      → { marqueId, nom, modeles: [{ nom }] }  ← référentiel CIBLE
//  - /api/parametre/type/MARQUE → { libelleParametre }                    ← ancien, SANS modèles
// La tolérance à l'ancienne forme évite un crash si un écran n'a pas encore migré,
// mais dans ce cas le Type n'aura aucune option (pas de `modeles`).
const normalizeMarques = (marques = []) =>
    (Array.isArray(marques) ? marques : [])
        .map((m) => ({ ...m, nom: m?.nom || m?.libelleParametre, marqueId: m?.marqueId }))
        .filter((m) => m.nom);

/** Retrouve la marque sélectionnée (comparaison insensible à la casse). */
export const findMarque = (marques, marque) =>
    normalizeMarques(marques).find(
        (m) => (m.nom || "").toUpperCase() === (marque || "").toUpperCase()
    );

/**
 * Select MARQUE, avec recherche.
 * ⚠ L'appelant DOIT réinitialiser le `type` quand la marque change (les modèles
 * proposés dépendent de la marque) — cf. EnginForm.handleMarqueChange.
 */
export const MarqueSelect = ({
    marques = [],
    value,
    onChange,
    disabled,
    placeholder = "Sélectionner la marque",
    ...rest
}) => {
    const options = useMemo(
        () =>
            normalizeMarques(marques).map((m) => ({
                label: m.nom,
                value: m.nom,
                key: m.marqueId || m.nom,
            })),
        [marques]
    );

    return (
        <Select
            value={value || undefined}
            onChange={onChange}
            placeholder={placeholder}
            showSearch
            optionFilterProp="label"
            options={options}
            disabled={disabled}
            {...rest}
        />
    );
};

/**
 * AutoComplete TYPE (modèle) — DÉPEND de la marque :
 *  - propose les modèles de la marque sélectionnée,
 *  - reste DÉSACTIVÉ tant qu'aucune marque n'est choisie,
 *  - met la valeur en MAJUSCULES,
 *  - à la sortie du champ, un type saisi mais inconnu est PERSISTÉ dans le référentiel
 *    (`/api/vehicule-marques/{marqueId}/modeles`) : la liste s'enrichit d'elle-même.
 */
export const TypeAutoComplete = ({
    marques = [],
    marque,
    value,
    onChange,
    disabled,
    ...rest
}) => {
    const selectedMarque = useMemo(() => findMarque(marques, marque), [marques, marque]);

    const options = useMemo(
        () =>
            (selectedMarque?.modeles || [])
                .filter((m) => m?.nom)
                .map((m) => ({ label: m.nom, value: m.nom })),
        [selectedMarque]
    );

    const persistModele = async (val) => {
        const nom = (val || "").trim().replace(/\s+/g, " ").toUpperCase();
        // Sans marqueId (ancienne forme de marque), on ne peut rien persister.
        if (!nom || !selectedMarque?.marqueId) return;
        const exists = (selectedMarque.modeles || []).some(
            (m) => (m?.nom || "").toUpperCase() === nom
        );
        if (exists) return;
        try {
            await axiosInstance.post(`/api/vehicule-marques/${selectedMarque.marqueId}/modeles`, { nom });
        } catch (e) {
            console.error("Erreur lors de l'enregistrement du modèle:", e);
        }
    };

    const emit = (v) => onChange?.((v || "").toUpperCase());

    return (
        <AutoComplete
            value={value}
            onChange={emit}
            onSelect={emit}
            onBlur={() => persistModele(value)}
            options={options}
            placeholder={selectedMarque ? "Sélectionner ou saisir le type" : "Sélectionner d'abord la marque"}
            filterOption={(input, opt) =>
                (opt?.value || "").toUpperCase().includes((input || "").toUpperCase())
            }
            disabled={disabled || !selectedMarque}
            {...rest}
        />
    );
};
